import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Link, IconButton, TextField, MenuItem, Chip, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';
import { useDispatch, useSelector } from 'react-redux';
import { updateAccount, activateAccount, deactivateAccount } from '../redux/ducks/account';
import { setTagAssignments } from '../redux/ducks/tag';
import TagPicker, { tagSourceNames, useTagDraft } from './TagPicker';
import UserMenuChip from './UserMenuChip';
import { Edit, Check, Close, Star, StarOutline } from '@mui/icons-material';
import { updatePinnedAccounts } from '../redux/slices/users';
import { useTheme } from '@mui/material/styles';
import ActiveChip from './ActiveChip';
import PrivacyDropdown from './PrivacyDropdown';
import { statusColor } from '../utils/statusColors';
import { formatDateTime } from '../utils/dateUtils';

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const ContainerBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.paper,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(4),
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(5),
    },
}))

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
}))

const TitleBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
}))

const TitleText = styled(Typography)({
    fontWeight: 'bold',
    minWidth: 0,
    overflowWrap: 'break-word',
})

const EditBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}))

const PinIcon = styled(Star)({
    cursor: 'pointer',
})

const PinOutlineIcon = styled(StarOutline)({
    cursor: 'pointer',
})

const PrivacyDot = styled(Box)(({ theme }) => ({
    width: theme.spacing(1.5),
    minWidth: theme.spacing(1.5),
    height: theme.spacing(1.5),
    borderRadius: '50%',
}))

const AccountInfoGridBox = (account) => {
    const { accountData } = account;
    const [currentStatus, setCurrentStatus] = useState(accountData.status);
    const [currentActive, setCurrentActive] = useState(accountData.active);
    const [currentBillingTerms, setCurrentBillingTerms] = useState(accountData.billingTerms);
    const [currentPrivacy, setCurrentPrivacy] = useState(accountData.privacy);
    const [editedStatus, setEditedStatus] = useState(accountData.status);
    const [editedActive, setEditedActive] = useState(accountData.active);
    const [editedBillingTerms, setEditedBillingTerms] = useState(accountData.billingTerms);
    const [editedPrivacy, setEditedPrivacy] = useState(accountData.privacy);
    const [currentTags, setCurrentTags] = useState(accountData.tags || []);
    const [tagsStale, setTagsStale] = useState(!!accountData.tagsStale);
    const pinnedAccounts = useSelector((state) => state.auth.currentUser?.preferences?.pinnedProjects || []);
    const [isEditing, setIsEditing] = useState(false);
    const [tagsSaving, setTagsSaving] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const hasPermission = useHasPermission();
    const canEditAccount = hasPermission("editAccount");
    const canEditBillingTerms = hasPermission("editBillingTerms");
    const isLocked = useIsLocked(accountData, accountData?.client);
    const clientInactive = accountData?.client?.active === false;
    const accountInactive = accountData?.active === false;
    const inactiveLabel = clientInactive && !accountInactive ? 'Client Inactive' : 'Inactive';
    const tagsEditable = isEditing && !accountData.isDeleted;
    const tagDraft = useTagDraft({ enabled: tagsEditable, targetType: 'Account', targetId: accountData._id, tags: currentTags, prefillInherited: tagsStale });
    const dispatch = useDispatch();

    useEffect(() => {
        setCurrentStatus(accountData.status);
        setCurrentActive(accountData.active);
        setCurrentBillingTerms(accountData.billingTerms);
        setCurrentPrivacy(accountData.privacy);
        setCurrentTags(accountData.tags || []);
        setTagsStale(!!accountData.tagsStale);
    }, [accountData]);

    const handleEdit = () => {
        setEditedStatus(currentStatus);
        setEditedActive(currentActive);
        setEditedBillingTerms(currentBillingTerms);
        setEditedPrivacy(currentPrivacy);
        setIsEditing(true);
    };

    const handleSave = () => {
        const billingTermsToSend = { type: editedBillingTerms.type };

        if (editedBillingTerms.type === 'Retainer') {
            billingTermsToSend.hours = editedBillingTerms.hours ? parseInt(editedBillingTerms.hours) : 0;
            billingTermsToSend.frequency = editedBillingTerms.frequency;
            billingTermsToSend.overage = editedBillingTerms.overage === 'true' || editedBillingTerms.overage === true;
        }

        dispatch(
            updateAccount(accountData._id, {
                status: editedStatus,
                billingTerms: billingTermsToSend,
                privacy: editedPrivacy
            }, (response) => {
                if (response?.account) {
                    setCurrentStatus(response.account.status);
                    setCurrentActive(response.account.active);
                    setCurrentBillingTerms(response.account.billingTerms);
                    setCurrentPrivacy(response.account.privacy);
                }
            })
        );

        const saveTags = tagsEditable && tagDraft.dirty;
        if (saveTags) {
            setTagsSaving(true);
            dispatch(setTagAssignments('Account', accountData._id, tagDraft.assignments, (data) => {
                setTagsSaving(false);
                if (!data) return;
                setCurrentTags(data.tags || []);
                if (tagDraft.assignments.inheritedTags) setTagsStale(false);
                setIsEditing(false);
            }));
        }

        if (currentActive !== editedActive) {
            dispatch(editedActive ? activateAccount(accountData._id, (response) => {
                if (response.status === 1) {
                    setCurrentActive(true);
                }
            }) : deactivateAccount(accountData._id, (response) => {
                if (response.status === 1) {
                    setCurrentActive(false);
                }
            }));
        }
        if (!saveTags) setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedStatus(currentStatus);
        setEditedActive(currentActive);
        setEditedBillingTerms(currentBillingTerms);
        setEditedPrivacy(currentPrivacy);
    };

    const handlePinAccount = () => {
        dispatch(updatePinnedAccounts({ accountId: accountData._id }));
    };

    return (
        <ContainerBox>
            <HeaderRow>
                <TitleBox>
                    {pinnedAccounts.includes(accountData._id) ? <PinIcon color="primary" onClick={handlePinAccount} /> : <PinOutlineIcon color="disabled" onClick={handlePinAccount} />}
                    {currentPrivacy && currentPrivacy !== "Public" && (
                        <Tooltip title={currentPrivacy}>
                            <PrivacyDot bgcolor={statusColor(theme, "privacy", currentPrivacy)} />
                        </Tooltip>
                    )}
                    <TitleText variant="h6">
                        {accountData.name}
                    </TitleText>
                    {(accountInactive || clientInactive) && (
                        <Chip color="warning" variant="outlined" size="small" label={inactiveLabel} />
                    )}
                </TitleBox>
                {canEditAccount && !isLocked &&
                    (isEditing ? (
                        <Box display="flex" gap={2}>
                            <IconButton onClick={handleSave} disabled={tagDraft.loading || tagsSaving}>
                                <Check />
                            </IconButton>
                            <IconButton onClick={handleCancel}>
                                <Close />
                            </IconButton>
                        </Box>
                    ) : (
                        <IconButton onClick={handleEdit}>
                            <Edit />
                        </IconButton>
                    ))}
            </HeaderRow>
            <Grid container spacing={{ xs: 2, md: 3, lg: 4 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Client
                    </Typography>
                    <StyledLink onClick={() => navigate(`/client/${accountData.client.slug}`)} variant="subtitle1" fontWeight="medium">
                        {accountData.client.name}
                    </StyledLink>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Account Manager
                    </Typography>
                    <StyledLink href={`tel:${accountData.phone}`}>{accountData.phone}</StyledLink>
                    <UserMenuChip user={accountData.accountManager} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Active
                    </Typography>
                    {canEditAccount && !isLocked && isEditing ? (
                        <TextField value={editedActive} onChange={(e) => setEditedActive(e.target.value)} fullWidth select>
                            <MenuItem value={true}>Active</MenuItem>
                            <MenuItem value={false}>Inactive</MenuItem>
                        </TextField>
                    ) : (
                        <Box display="flex" alignItems="center">
                            <ActiveChip status={currentActive ? "Active" : "Inactive"} />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Last Update
                    </Typography>
                    <Typography>{formatDateTime(accountData.updatedAt)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Billing Type
                    </Typography>
                    {canEditBillingTerms && canEditAccount && !isLocked && isEditing ? (
                        <TextField value={editedBillingTerms.type} onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, type: e.target.value })} fullWidth select>
                            <MenuItem value="Hourly">Hourly</MenuItem>
                            <MenuItem value="Retainer">Retainer</MenuItem>
                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                            <MenuItem value="Parent">Parent</MenuItem>
                        </TextField>
                    ) : (
                        <Typography>{currentBillingTerms?.type === "Parent" ? "Inherited from Client" : currentBillingTerms?.type}</Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                        Terms
                    </Typography>
                    {canEditBillingTerms && canEditAccount && !isLocked && isEditing ? (
                        editedBillingTerms?.type === "Retainer" && (
                            <EditBox>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    value={editedBillingTerms.hours}
                                    onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, hours: e.target.value })}
                                    fullWidth
                                />
                                <TextField
                                    label="Frequency"
                                    value={editedBillingTerms.frequency}
                                    onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, frequency: e.target.value })}
                                    fullWidth
                                    select
                                >
                                    <MenuItem value="Monthly">Monthly</MenuItem>
                                    <MenuItem value="Weekly">Weekly</MenuItem>
                                </TextField>
                                <TextField
                                    label="Overage"
                                    value={editedBillingTerms.overage}
                                    onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, overage: e.target.value })}
                                    fullWidth
                                    select
                                >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </TextField>
                            </EditBox>
                        )
                    ) : (
                        <>
                            {currentBillingTerms?.type === "Retainer" && (
                                <Typography>
                                    {currentBillingTerms?.hours} Hrs {currentBillingTerms?.frequency} / {currentBillingTerms?.overage ? "Overage Allowed" : "No Overage"}
                                </Typography>
                            )}
                            {currentBillingTerms?.type === "Fixed Price" && (
                                <Typography>
                                    $ {currentBillingTerms?.charge} / {currentBillingTerms?.hours} Hrs{" "}
                                </Typography>
                            )}
                            {currentBillingTerms?.type === "Parent" && <Typography>Inherited from Client</Typography>}
                        </>
                    )}
                </Grid>
                {canEditAccount && !isLocked && isEditing && (
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                            Privacy
                        </Typography>
                        <PrivacyDropdown value={editedPrivacy} onChange={(e) => setEditedPrivacy(e.target.value)} />
                    </Grid>
                )}
                {(tagsEditable || currentTags.length > 0) && (
                    <Grid item xs={12}>
                        {tagsEditable ? (
                            <TagPicker draft={tagDraft} targetType="Account" targetId={accountData._id} clientId={accountData.client?._id || accountData.client || null} clientName={accountData.client?.name} sourceNames={tagSourceNames(accountData.client)} />
                        ) : (
                            <TagPicker value={currentTags} targetType="Account" targetId={accountData._id} sourceNames={tagSourceNames(accountData.client)} readOnly />
                        )}
                    </Grid>
                )}
            </Grid>
        </ContainerBox>
    );
}

export default AccountInfoGridBox;